// src/hooks/useCredits.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { getAccessToken } from "@/lib/authToken";
import type { CreditsBalance, CreditsSummary } from "@/types/credits";

const BALANCE_KEY = ["/api/credits/balance"];
const SUMMARY_KEY = ["/api/credits/summary"];
const PROFILE_KEY = ["/api/user/profile"];

export function useCreditsBalance() {
  return useQuery<CreditsBalance>({
    queryKey: BALANCE_KEY,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/credits/balance");
      if (!res.ok) throw new Error(await res.text());
      return (await res.json()) as CreditsBalance;
    },
    enabled: !!getAccessToken(),
    // Ensure UI updates promptly after purchases without manual refresh
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useCreditsSummary() {
  return useQuery<CreditsSummary>({
    queryKey: SUMMARY_KEY,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/credits/summary");
      if (!res.ok) throw new Error(await res.text());
      return (await res.json()) as CreditsSummary;
    },
    enabled: !!getAccessToken(),
    refetchInterval: 10000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useMockPurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { credits?: number }) => {
      const res = await apiRequest("POST", "/api/credits/mock-purchase", payload ?? {});
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BALANCE_KEY });
      qc.invalidateQueries({ queryKey: SUMMARY_KEY });
      qc.invalidateQueries({ queryKey: PROFILE_KEY });
    },
  });
}